import { defineCard } from '@cards/engine';
import { compileAbilities } from './abilities/compose.js';
import { isPixelGlyph } from './pixel-glyph.js';
import { normalizeGameCardUi } from './ui/normalize.js';
const CONTENT_ROLES = ['playable', 'auxiliary', 'standalone_lab', 'legacy'];
const GAMEPLAY_KEYS = new Set([
    'id',
    'hooks',
    'type',
    'baseStats',
    'cost',
    'minimumCost',
    'effectiveCost',
    'keywords',
    'traits',
    'combatDamageParts',
    'combatDamageOverflow',
    'planning',
]);
const PRESENTATION_KEYS = new Set(['id', 'name', 'description', 'glyph']);
function assertId(id, owner) {
    if (typeof id !== 'string' || id.length === 0) {
        throw new Error(`${owner}: id должен быть непустой строкой`);
    }
}
function assertOnlyKeys(value, allowed, owner) {
    for (const key of Reflect.ownKeys(value)) {
        if (!allowed.has(key))
            throw new Error(`${owner}: неизвестное поле "${String(key)}"`);
    }
}
function normalizeDependencies(ownerId, dependencies = []) {
    const seen = new Set();
    return Object.freeze(dependencies.map((dependency) => {
        assertId(dependency.id, `defineGameCard "${ownerId}" dependency`);
        if (seen.has(dependency.id)) {
            throw new Error(`defineGameCard "${ownerId}": повторная dependency "${dependency.id}"`);
        }
        seen.add(dependency.id);
        return Object.freeze({ id: dependency.id });
    }));
}
function mergeDependencies(ownerId, declared, inferred) {
    const normalizedDeclared = normalizeDependencies(ownerId, declared);
    const seen = new Set(normalizedDeclared.map(({ id }) => id));
    const merged = [...normalizedDeclared];
    for (const dependency of inferred) {
        assertId(dependency.id, `defineGameCard "${ownerId}" ability dependency`);
        if (seen.has(dependency.id))
            continue;
        seen.add(dependency.id);
        merged.push(Object.freeze({ id: dependency.id }));
    }
    return Object.freeze(merged);
}
export function cardRef(id) {
    assertId(id, 'cardRef');
    return Object.freeze({ id });
}
export function defineGameCard(input) {
    assertId(input.id, 'defineGameCard');
    if (!CONTENT_ROLES.includes(input.role)) {
        throw new Error(`defineGameCard "${input.id}": неизвестная role "${String(input.role)}"`);
    }
    if (input.presentation.name.length === 0 || input.presentation.description.length === 0) {
        throw new Error(`defineGameCard "${input.id}": name и description должны быть непустыми`);
    }
    assertOnlyKeys(input.gameplay, GAMEPLAY_KEYS, `defineGameCard "${input.id}" gameplay`);
    assertOnlyKeys(input.presentation, PRESENTATION_KEYS, `defineGameCard "${input.id}" presentation`);
    if (!isPixelGlyph(input.presentation.glyph)) {
        throw new Error(`defineGameCard "${input.id}": некорректный glyph`);
    }
    const runtimeGameplay = input.gameplay;
    const { id: ignoredGameplayId, hooks: ignoredGameplayHooks, ...gameplay } = runtimeGameplay;
    void ignoredGameplayId;
    void ignoredGameplayHooks;
    const compiled = compileAbilities(input.id, input.abilities, input.hooks);
    const ui = normalizeGameCardUi(input.ui, `defineGameCard "${input.id}"`);
    const publicVarKeys = [...new Set(Object.values(ui.publicVars ?? {}).map((projection) => projection.key))];
    const definition = defineCard({
        ...gameplay,
        ...(compiled.hooks === undefined ? {} : { hooks: compiled.hooks }),
        ...(publicVarKeys.length === 0 ? {} : { publicVarKeys: Object.freeze(publicVarKeys) }),
        id: input.id,
    });
    const runtimePresentation = input.presentation;
    const { id: ignoredPresentationId, ...presentation } = runtimePresentation;
    void ignoredPresentationId;
    return Object.freeze({
        id: input.id,
        definition,
        presentation: Object.freeze({ ...presentation, id: input.id }),
        ui,
        role: input.role,
        dependencies: mergeDependencies(input.id, input.dependencies, compiled.dependencies),
        abilityState: compiled.state,
        abilityMeta: compiled.meta,
        source: 'game_card',
    });
}
//# sourceMappingURL=define-game-card.js.map